import React, { useRef, useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
// import Sidebar from "./Sidebar"; // Sidebar importieren
// import * as THREE from 'three'; // Beibehalten, falls es im Projekt global benötigt wird - HINWEIS: THREE.js wird hier nicht verwendet, kann entfernt werden wenn nicht anderweitig global in Ihrem Projekt genutzt

const MODEL_URL = "https://teachablemachine.withgoogle.com/models/XYjE70sA2/model.json"; // Gleiches Modell wie Level 1
const METADATA_URL = "https://teachablemachine.withgoogle.com/models/XYjE70sA2/metadata.json"; // Gleiche Metadaten
const LEVEL2_PASSWORD = "LABEL";

type Prediction = {
    label: string;
    confidence: number;
};
type Props = {
    onNextLevel: () => void;
    onBack: () => void;
    avatarImage: string;
    groupName: string;
    initialLevelStep?: number;
    unlocked?: boolean; // <--- NEU
    unlockLevel?: () => void; // <--- NEU
};



export default function Level2({ onNextLevel, onBack, avatarImage, groupName, initialLevelStep, unlocked, unlockLevel }: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // All state that should be reset
    const [model, setModel] = useState<tf.LayersModel | null>(null);
    const [labels, setLabels] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [videoReady, setVideoReady] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [currentPredictions, setCurrentPredictions] = useState<Prediction[]>([]);
    const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
    const [levelStep, setLevelStep] = useState(initialLevelStep ?? 0);
    const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(!unlocked);
    const [passwordInput, setPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState("");

    useEffect(() => {
        async function loadModelAndMetadata() {
            setLoading(true);
            try {
                const loadedModel = await tf.loadLayersModel(MODEL_URL);
                setModel(loadedModel);
                const metadataResponse = await fetch(METADATA_URL);
                const metadata = await metadataResponse.json();
                setLabels(metadata.labels);

                // WARM UP: Run a dummy prediction to compile the model
                tf.tidy(() => {
                    const dummy = tf.zeros([1, 224, 224, 3]);
                    (loadedModel.predict(dummy) as tf.Tensor).dataSync();
                });
            } catch (err) {
                console.error("Fehler beim Laden des Modells oder der Metadaten", err);
            } finally {
                setLoading(false);
            }
        }
        loadModelAndMetadata();
    }, []);

    useEffect(() => {
        // Kamera nur aktivieren, wenn Test-Modal offen ist und Modell geladen ist
        if (!showTestModal || !model) {
            setVideoReady(false);
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
            return;
        }

        async function setupCamera() {
            if (!videoRef.current) return;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        videoRef.current.play();
                        setVideoReady(true);
                    }
                };
            } catch (err) {
                console.error("Fehler beim Zugriff auf die Kamera:", err);
                setVideoReady(false);
                // Vielleicht hier eine Fehlermeldung im UI anzeigen
            }
        }

        setupCamera();

        return () => {
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
        };
    }, [showTestModal, model]); // Abhängigkeit von 'model' hinzugefügt

    const handleNewTestphotoClick = () => {
        setShowTestModal(true);
        setCapturedImageUrl(null); // Bild zurücksetzen, wenn neues Foto gemacht wird
        setCurrentPredictions([]);
        setLevelStep(2); // Geht zum Frame 46
    };

    const captureAndClassifyTestImage = async () => {
        if (!model || !videoRef.current || !canvasRef.current || !videoReady || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
            console.warn("Kann kein Bild aufnehmen: Modell nicht geladen, Kamera nicht bereit oder Video-Dimensionen 0.");
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        setCapturedImageUrl(canvas.toDataURL('image/png'));

        let imgTensor = tf.browser.fromPixels(canvas);
        imgTensor = tf.image.resizeBilinear(imgTensor, [224, 224]).div(255).expandDims(0);

        const predictionTensor = model.predict(imgTensor) as tf.Tensor;
        const predictionData = await predictionTensor.data();
        tf.dispose([imgTensor, predictionTensor]);

        const predictions = labels.map((label, i) => ({
            label,
            confidence: predictionData[i],
        })).sort((a, b) => b.confidence - a.confidence);

        setCurrentPredictions(predictions);
        setShowTestModal(false);
        setShowAnalyzeModal(true);

        setTimeout(() => {
            setShowAnalyzeModal(false);
            setShowResultModal(true);
            setLevelStep(3);
        }, 1500);
    };

    const handleResultModalClose = () => {
        setShowResultModal(false);
        setLevelStep(4); // Geht zum Frame 50
    };

    // Reset all state to initial values
    const resetLevel = () => {
        setLevelStep(0);
        setShowTestModal(false);
        setShowResultModal(false);
        setCurrentPredictions([]);
        setCapturedImageUrl(null);
        setVideoReady(false);
        // Optionally reload model/labels if you want a full remount effect
        // setModel(null);
        // setLabels([]);
        // setLoading(true);
    };

    // Helper for consistent button row styling
        const buttonRowStyle: React.CSSProperties = {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginTop: '20px',
            gap: '10px',
        };

    // Password modal logic
    if (showPasswordModal) {
        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    padding: "40px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    minWidth: "320px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px"
                }}>
                    <h2 style={{ marginBottom: 10, color: "#333" }}>Passwort eingeben für Level 2</h2>
                    <p style={{ color: "#555", fontSize: "1.1em", textAlign: "center" }}>
                        Gebt hier das Passwort ein, um zu Level 2 zu gelangen:
                    </p>
                    <input
                        type="password"
                        value={passwordInput}
                        onChange={e => { setPasswordInput(e.target.value); setPasswordError(""); }}
                        style={{
                            padding: "10px",
                            fontSize: "1.2em",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                            width: "200px",
                            marginBottom: "10px"
                        }}
                        autoFocus
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                if (passwordInput.trim().toUpperCase() === LEVEL2_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }
                        }}
                    />
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "15px"
                    }}>
                        <button
                            onClick={onBack}
                            style={{
                                backgroundColor: "#f0f0f0",
                                color: "#555",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            onClick={() => {
                                if (passwordInput.trim().toUpperCase() === LEVEL2_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }}
                            style={{
                                backgroundColor: "#4c84ff",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Bestätigen
                        </button>
                    </div>
                    {passwordError && (
                        <div style={{ color: "red", fontWeight: 600 }}>{passwordError}</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            fontFamily: 'Nunito, sans-serif',
            backgroundColor: '#f7f9fc',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '30px',
            gap: '30px',
            position: 'relative'
        }}>

            <div style={{
                flexGrow: 1,
                backgroundColor: '#fff',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative', // Needed for absolute positioning of the button
            }}>
                {/* Restart Button top right INSIDE the white card, styled like Level 1 */}
                <button
                    onClick={resetLevel}
                    style={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '8px 18px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                        zIndex: 10,
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    🔄 Level neu starten
                </button>

                {/* Header-Bereich mit Avatar und Level-Titel - übernommen von Level 1 */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '4px solid #4c84ff',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        marginRight: '20px',
                        flexShrink: 0,
                    }}>
                        <img src={avatarImage} alt="Team Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>

                    <div style={{ flexGrow: 1 }}>
                        <h2 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '10px' }}>
                            Level 2: Wenig gesehen, wenig gelernt
                        </h2>
                        <p style={{ fontSize: '1.4em', color: '#555' }}>
                            Testet die KI mit neuen Bildern!
                        </p>
                    </div>

                    {/* Level Zurück Button */}
                    

                </div>

                {/* Phasenabhängiger Inhalt */}
                {levelStep === 0 && ( // Frame 44
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '20px',
                        backgroundColor: '#e6f7ff',
                        border: '2px solid #91d1ff',
                        borderRadius: '10px',
                        padding: '25px',
                        maxWidth: '700px', // etwas breiter für den Text
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        marginTop: '30px',
                    }}>
                        <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                            Jetzt kommt der Test! 📸 <br />
                            Macht ein neues Foto von einem Apfel oder einer Banane. Mal sehen, ob ich den Unterschied erkenne! Ich bin noch ein bisschen unsicher… Ich hab nämlich erst wenige Bilder gesehen und muss noch viel üben. 😬
                        </p>
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ flex: 1 }} />
                            <button
                                onClick={handleNewTestphotoClick}
                                style={{
                                    marginTop: '20px',
                                    backgroundColor: '#4c84ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}
                            >
                                Weiter
                            </button>
                        </div>
                        
                    </div>
                )}

                {/* {levelStep === 1 && ( // Frame 45
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '20px',
                        backgroundColor: '#e6f7ff',
                        border: '2px solid #91d1ff',
                        borderRadius: '10px',
                        padding: '25px',
                        maxWidth: '700px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        marginTop: '30px',
                    }}>
                        <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                            Drückt auf “Neues Testbild aufnehmen” und schießt ein Testfoto, damit ich euch zeigen kann, was ich schon gelernt habe. Macht ein Foto von eurem Apfel oder eurer Banane und erzählt danach in eurer Klasse, wie gut ich die Objekte erkannt habe!
                        </p>
                        <div style={buttonRowStyle}>
                        <button
                                onClick={() => {
                                    // Geht zum vorherigen Schritt (Frame 44)
                                    setLevelStep(prev => Math.max(0, prev - 1));
                                }}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                           
                        <button onClick={handleNewTestphotoClick} style={{ // Öffnet das Test-Modal
                            marginTop: '20px',
                            backgroundColor: '#4c84ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 25px',
                            cursor: 'pointer',
                            fontSize: '1.1em',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            transition: 'background-color 0.2s ease',
                        }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}>
                            Neues Testbild aufnehmen
                        </button> 
                        </div>
                    </div>
                )} */}

                {levelStep === 4 && ( // Frame 50 - Nach dem Ergebnis-Modal
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '20px',
                        backgroundColor: '#e6f7ff',
                        border: '2px solid #91d1ff',
                        borderRadius: '10px',
                        padding: '25px',
                        maxWidth: '700px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        marginTop: '30px',
                    }}>
                        <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                            Super gemacht, Gruppe "{groupName}"! 😊 <br />
                            Klickt "Weiter", um zu Level 3 zu gelangen.
                        </p>

                        <div style={buttonRowStyle}>
                        <button
                                onClick={() => {
                                    setShowResultModal(true);
                                    setLevelStep(3);
                                }}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                        <button onClick={onNextLevel} style={{ // <--- HIER WIRD onNextLevel AUFGERUFEN
                            marginTop: '20px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 25px',
                            cursor: 'pointer',
                            fontSize: '1.1em',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            transition: 'background-color 0.2s ease',
                        }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#45a049'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4caf50'}>
                            Weiter
                        </button>
                        </div>
                    </div>
                )}


                {/* Test-Modal (Frame 46) */}
                {showTestModal && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '15px',
                            padding: '40px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                            maxWidth: '700px',
                            width: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '25px',
                            position: 'relative',
                        }}>
                            <h3 style={{ fontSize: '1.8em', color: '#333', marginBottom: '15px', textAlign: 'center' }}>
                                Level 2: Wenig gesehen, wenig gelernt
                            </h3>
                            <p style={{ fontSize: '1.2em', color: '#555', textAlign: 'center' }}>
                                Nehmt ein neues Testbild auf!
                            </p>
                            <video
                                ref={videoRef}
                                style={{ border: "3px solid #ddd", borderRadius: '10px', maxWidth: '100%', maxHeight: '400px', display: videoReady ? 'block' : 'none' }}
                                autoPlay
                                muted
                                playsInline
                            />
                            {!videoReady && (
                                <div style={{width: '100%', height: '300px', backgroundColor: '#eee', borderRadius: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#888'}}>
                                    Kamera wird geladen...
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
                                <button onClick={() => { 
                                    setShowTestModal(false); 
                                    setLevelStep(0); 
                                }} style={{
                                    backgroundColor: '#d9534f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#c9302c'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#d9534f'}>
                                    Abbrechen
                                </button>
                                <button onClick={captureAndClassifyTestImage} disabled={!videoReady || loading} style={{
                                    backgroundColor: '#4c84ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: (!videoReady || loading) ? 'not-allowed' : 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                    opacity: (!videoReady || loading) ? 0.6 : 1,
                                }} onMouseOver={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#3b6dd6')} onMouseOut={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#4c84ff')}>
                                    Bild aufnehmen
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Ergebnis-Modal (Frame 47) */}
                {showResultModal && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '15px',
                            padding: '40px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                            maxWidth: '700px',
                            width: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px',
                            position: 'relative',
                        }}>
                            <h3 style={{ fontSize: '1.8em', color: '#333', textAlign: 'center' }}>
                                Ergebnisse:
                            </h3>
                            {/* Hier wird das aufgenommene Bild angezeigt */}
                            {capturedImageUrl && (
                                <img src={capturedImageUrl} alt="Analyzed Test Object" style={{ maxWidth: '150px', maxHeight: '150px', marginBottom: '15px', borderRadius: '10px', border: '2px solid #ddd' }} />
                            )}
                            {!capturedImageUrl && (
                                <div style={{width: '150px', height: '150px', backgroundColor: '#eee', borderRadius: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#888', marginBottom: '15px'}}>
                                    Kein Bild
                                </div>
                            )}

                            {currentPredictions.map((prediction, index) => (
                                <div key={index} style={{ width: '100%', marginBottom: '10px' }}>
                                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '5px' }}>
                                        {prediction.label}: {Math.round(prediction.confidence * 100)}%
                                    </p>
                                    <div style={{
                                        width: '100%',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '5px',
                                        height: '15px',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            width: `${prediction.confidence * 100}%`,
                                            height: '100%',
                                            backgroundColor: prediction.label === "Apfel" ? '#FFD700' : '#4CAF50', // Annahme: Apfel=Gelb, Banane=Grün - passen Sie dies an Ihre Labels an
                                            borderRadius: '5px',
                                        }} />
                                    </div>
                                </div>
                            ))}

                            {/* Warnhinweis */}
                            <p style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontSize: '1.1em',
                                marginTop: '10px',
                                textAlign: 'center',
                            }}>
                                Macht einen Screenshot vom Ergebnis!
                            </p>

                            {/* Footer with two buttons */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between', // <-- changed from 'flex-end' to 'space-between'
                                width: '100%',
                                gap: '15px',
                                marginTop: '10px'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowResultModal(false);
                                        setCapturedImageUrl(null);
                                        setCurrentPredictions([]);
                                        setShowTestModal(true);
                                        setVideoReady(false);
                                    }}
                                    style={{
                                        backgroundColor: '#f0f0f0',
                                        color: '#555',
                                        border: '1px solid #ccc',
                                        borderRadius: '8px',
                                        padding: '12px 25px',
                                        cursor: 'pointer',
                                        fontSize: '1.1em',
                                        fontWeight: 600,
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                        transition: 'background-color 0.2s ease',
                                    }}
                                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                >
                                    Nochmal ausprobieren
                                </button>
                                <button onClick={handleResultModalClose} style={{
                                    backgroundColor: '#4c84ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}>
                                    Weiter
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showAnalyzeModal && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '15px',
                            padding: '40px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                            maxWidth: '700px',
                            width: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '25px',
                            position: 'relative',
                        }}>
                            <h3 style={{ fontSize: '1.8em', color: '#333', marginBottom: '15px', textAlign: 'center' }}>
                                Analysiere Bild...
                            </h3>
                            <div style={{ width: '80%', height: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#4c84ff',
                                    borderRadius: '5px',
                                    animation: 'loading-bar 1.5s forwards linear',
                                }} />
                            </div>
                            <style>
                                {`
                                @keyframes loading-bar {
                                    0% { width: 0%; }
                                    100% { width: 100%; }
                                }
                                `}
                            </style>
                        </div>
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
        </div>
    );
}